<template>
  <TransitionRoot as="template" :show="props.open">
    <Dialog
      as="div"
      class="relative z-30"
      :initialFocus="descriptionInput"
      @close="handleTaskDetailsClosed"
    >
      <div class="fixed-0 overflow-hidden">
        <div class="absolute overflow-hidden">
          <div class="fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-200"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
            >
              <DialogPanel class="pointer-events-auto w-screen max-w-xl">
                <div
                  id="scroll-container"
                  class="flex h-full flex-col overflow-y-scroll bg-white border-l border-l-gray-200"
                >
                  <div class="flex flex-col">
                    <div
                      class="sticky top-0 w-full max-w-xl top-0 px-6 pt-6 pb-4 bg-white z-50 border-b border-b-gray-200"
                    >
                      <div class="flex justify-between items-center">
                        <div class="flex flex-wrap items-center gap-2">
                          <div>
                            <primary-button
                              @click="handleCreateTask"
                              :label="t('components.buttons.create_task')"
                              class="border border-gray-500 text-gray-600 group flex items-center px-4 py-2 text-sm cursor-pointer font-medium rounded-md focus:ring-0 focus:outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <button
                            type="button"
                            class="rounded-sm bg-white text-gray-400 hover:text-gray-900 focus:outline-none transition"
                            @click="handleTaskDetailsClosed"
                          >
                            <span class="sr-only">Close panel</span>
                            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      <Errors class="mt-2" />

                      <textarea
                        rows="3"
                        :placeholder="t('validation.attributes.description')"
                        class="mt-6 w-full font-medium resize-none border-transparent focus:border-transparent focus:ring-0 p-0"
                        v-model="selected.description"
                        ref="descriptionInput"
                      ></textarea>
                    </div>
                    <div>
                      <div
                        class="grid grid-cols-3 gap-4 px-6 pt-4"
                        v-if="selected.photos.length > 0"
                      >
                        <MediaPreview
                          v-for="photo in selected.photos"
                          :key="photo.id"
                          class="border border-gray-200"
                          :item="photo"
                        />
                      </div>
                      <div class="flex p-6">
                        <MediaSelector
                          :loading="isProcessingAddingImages"
                          @selected="handleMediaAdded"
                          alt-style
                        />
                      </div>
                    </div>
                    <div class="flex flex-col space-y-8 p-6">
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.space") }}</span
                          >
                          <LocationDropdown
                            class="grow"
                            disable-ungrouped
                            disable-tenants
                            :tenants="filteredTenants"
                            :model-value="selectedSpace"
                            @update:model-value="handleSpaceSelected"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.assignee") }}</span
                          >
                          <MultipleUsers
                            :tenant-id="tenantId"
                            :user-ids="selected.assignee_ids"
                            :disabled="!tenantId"
                            blank
                            @change="handleAssigneeSelected"
                            :horizontalLabelWidth="'8rem'"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("validation.attributes.start_at") }}</span
                          >
                          <DueDate
                            blank
                            :schedule-date="selected.date_scheduled"
                            @change="handleDueDateSelected"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.category") }}</span
                          >
                          <CategorySelector
                            :category-id="selected.category_id"
                            :tenant-id="tenantId"
                            :disabled="!tenantId"
                            @change="handleCategorySelected"
                            :horizontalLabelWidth="'8rem'"
                          />
                        </div>
                      </div>
                      <div>
                        <div class="flex items-center">
                          <span
                            class="font-semibold text-gray-500 text-sm w-32"
                            >{{ t("admin.search.filters.priority") }}</span
                          >
                          <DropdownSelector
                            @change="handlePrioritySelected"
                            :value="selected.priority"
                            :options="priorities"
                            :label="t('admin.search.filters.category')"
                            :horizontalLabelWidth="'8rem'"
                          >
                            <template v-slot:icon>
                              <ExclamationTriangleIcon
                                class="h-5 w-5 text-gray-500 group-hover:text-gray-900"
                              />
                            </template>
                          </DropdownSelector>
                        </div>
                      </div>
                    </div>

                    <hr />

                    <div>
                      <div class="flex flex-col p-6">
                        <span
                          class="font-semibold text-gray-700 text-base w-32"
                          >{{
                            t("admin.settings.recurring_tasks.recurrence")
                          }}</span
                        >
                        <RRuleBuilder
                          :rule="parseRRuleString"
                          class="py-4"
                          @change="handleRRuleChanged"
                        />
                      </div>
                    </div>

                    <div>
                      <hr />
                      <div>
                        <div class="flex flex-col p-6 gap-2">
                          <div class="flex justify-between">
                            <span class="font-semibold text-gray-700 text-base">
                              {{ t("admin.settings.checklist.title") }}
                            </span>
                          </div>
                          <TextAreaWithLabel
                            id="checklist_items"
                            :modelValue="selected.checklist_items.join(';')"
                            @update:modelValue="updateChecklistItems"
                            :placeholder="
                              t('admin.settings.checklist.input.placeholder')
                            "
                            :rows="3"
                            :disabled="!canCreateChecklist"
                          />
                          <div class="text-gray-700 text-sm">
                            {{
                              t("admin.settings.checklist.input.instructions")
                            }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr />

                    <div>
                      <div class="flex flex-col p-6">
                        <Errors class="mb-4" :errors="attachments?.errors" />
                        <FileInput
                          ref="attachments"
                          @input="handleAttachmentsSelected"
                          multiple
                          hidden
                        />
                        <div class="flex justify-between">
                          <span class="font-semibold text-gray-700 text-base">{{
                            t("admin.settings.documents.documents")
                          }}</span>
                          <div>
                            <secondary-button
                              @click="handleAddAttachments"
                              :label="t('components.buttons.new_document')"
                            />
                          </div>
                        </div>
                        <div
                          class="flex flex-col space-y-2 py-2"
                          v-if="selectedAttachments.length > 0"
                        >
                          <div
                            class="flex justify-between space-x-2 items-center bg-white border border-gray-200 rounded-md px-2 py-1"
                            v-for="attachment in selectedAttachments"
                            :key="attachment.id"
                          >
                            <img
                              v-if="attachment.src"
                              class="w-12 h-12 object-scale-down"
                              :src="attachment.src"
                              :alt="attachment.name"
                            />
                            <div v-else>
                              <div
                                class="w-12 h-12 flex items-center justify-center"
                              >
                                <FileFive class="w-5 h-5" />
                              </div>
                            </div>

                            <div
                              class="flex w-full flex-col text-sm overflow-hidden pr-12"
                            >
                              <span class="truncate text-ellipsis">{{
                                attachment.name
                              }}</span>
                              <span class="text-gray-500">{{
                                formatBytes(attachment.size)
                              }}</span>
                            </div>
                            <div>
                              <XMarkIcon
                                class="h-6 w-6 cursor-pointer text-gray-400 hover:text-gray-500"
                                aria-hidden="true"
                                @click="handleRemoveAttachment(attachment.id)"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, defineEmits, defineProps, inject, ref, watch } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import DueDate from "@/Tailwind/Components/Input/DueDate";
import CategorySelector from "@/Tailwind/Components/Input/CategorySelector";
import DropdownSelector from "@/Tailwind/Components/Input/DropdownSelector.vue";
import MediaPreview from "@/Tailwind/Components/MediaPreview.vue";
import MediaSelector from "@/Tailwind/Components/MediaSelector";
import { useI18n } from "vue-i18n";
import { usePage } from "@inertiajs/vue3";
import RRuleBuilder from "@/Tailwind/Components/RRule/RRuleBuilder.vue";
import { format, parseISO } from "date-fns";
import { RRule } from "rrule";
import { uniqueId, isEmpty } from "lodash";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import { includeHours } from "@/Tailwind/Helpers/time";
import FileFive from "@/Tailwind/Icon/FileFive.vue";
import { formatBytes } from "../../Helpers/size";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";
import Errors from "@/Tailwind/Components/Errors.vue";
import MultipleUsers from "@/Tailwind/Components/Input/MultipleUsers.vue";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import LocationDropdown from "@/Tailwind/Components/Input/LocationDropdown.vue";
import { FileInput } from "@/Components/Inputs";
import TextAreaWithLabel from "@/Components/Inputs/TextAreaWithLabel.vue";
const { t } = useI18n();

const tenantId = ref();

const emit = defineEmits([
  /** The details pane should be closed. */
  "close",

  /** A new recurring task should be created. */
  "save",
]);

const descriptionInput = ref(null);

const props = defineProps({
  /** Whether the task detail view should be open */
  open: Boolean,
});

const { tenantsWithUserPermission, hasPermission, permissions } =
  inject("permissions");

const filteredTenants = computed(() =>
  usePage().props.tenants.filter((tenant) => {
    return tenantsWithUserPermission(permissions.createRecurringTask)?.includes(
      tenant.id
    );
  })
);

const initialSelectedObject = {
  description: null,
  assignee_ids: [],
  priority: null,
  space_id: null,
  category_id: null,
  date_scheduled: null,
  rrule: null,
  photos: [],
  checklist_items: [],
};

const selected = ref(Object.assign({}, initialSelectedObject));

/** Determine which actions are currently being processed */
const isProcessingAddingImages = ref(false);

const attachments = ref(null);
const selectedAttachments = ref([]);

async function handleAttachmentsSelected(files) {
  for (const file of files) {
    selectedAttachments.value = [
      ...selectedAttachments.value,
      {
        id: uniqueId(),
        name: file.name,
        size: file.size,
        data: file,
        src: ["image/png", "image/jpg", "image/jpeg"].includes(file.type)
          ? URL.createObjectURL(file)
          : null,
      },
    ];
  }
}

function updateChecklistItems(items) {
  selected.value.checklist_items = items
    .split(";")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function handleRemoveAttachment(id) {
  selectedAttachments.value = selectedAttachments.value.filter(
    (attachment) => attachment.id !== id
  );
}

function handleAddAttachments() {
  attachments.value.click();
}

function handleRRuleChanged(rule) {
  selected.value.rrule = rule.value;
}

const canCreateChecklist = computed(() => {
  if (isEmpty(tenantId.value)) {
    return false;
  }
  return hasPermission(permissions.createChecklist, tenantId.value);
});

const parseRRuleString = computed(() => {
  if (!selected.value.date_scheduled) {
    return;
  }

  const startDate = format(
    parseISO(selected.value.date_scheduled.toISOString()),
    "yyyyMMdd'T'HHmmss"
  );

  if (!selected.value.rrule) {
    return;
  }

  const rule = "DTSTART:" + startDate + ";\n" + selected.value.rrule.toString();

  return RRule.fromString(rule);
});

function handleCreateTask() {
  emit("save", {
    ...selected.value,
    documents: selectedAttachments.value.map((attachment) => attachment.data),
  });
}

function handleMediaAdded(media) {
  selected.value = {
    ...selected.value,
    photos: [
      ...selected.value.photos,
      ...Array.from(media).map((item) => ({
        id: uniqueId(),
        type: item.type,
        name: item.name,
        data: item,
      })),
    ],
  };
}

function handleAssigneeSelected(users) {
  selected.value.assignee_ids = users.map((user) => user.id);
}

const priorities = usePage().props.priorities?.map((priority) => ({
  label: priority.name,
  value: priority.id,
}));

function handlePrioritySelected(priorityId) {
  selected.value.priority = priorityId;
}

function handleCategorySelected(categoryId) {
  selected.value.category_id = categoryId;
}

const selectedSpace = ref();
function handleSpaceSelected(space) {
  if (!space) {
    return;
  }

  if (selected.value.space_id !== space.id) {
    selected.value.assignee_ids = [];
    selected.value.category_id = null;
  }
  selectedSpace.value = space;

  selected.value.space_id = space.id;
  tenantId.value = space.tenant;
}

function handleDueDateSelected(date) {
  if (date) {
    selected.value.date_scheduled = includeHours(date);
  }
}

const reset = () => {
  selected.value = Object.assign({}, initialSelectedObject);
  selectedSpace.value = {};
  selectedAttachments.value = [];
  selectedSpace.value = null;
  selected.value.checklist_items = [];
};

watch(tenantId, () => {
  selected.value.checklist_items = [];
});

defineExpose({ reset });

function handleTaskDetailsClosed() {
  reset();
  emit("close");
}
</script>
